import { TRANSPORT_PAGES } from "@/router/pageNames";
import { defaultColumnsOrder, defaultColumnsVisibility } from "@/features/transportTableColumnsSettings/defaults";
import { migrateColumns, migrateOrder } from "@/features/transportTableColumnsSettings/migration";

const VERSION = 26;

export function loadColumnsOrder() {
  const storageItem = localStorage.getItem(columnsOrderKey());

  if (storageItem) {
    try {
      let orderSettings = JSON.parse(storageItem);
      if (orderSettings.version !== VERSION) {
        orderSettings = migrateOrder(orderSettings);
        saveColumnsOrder({ order: orderSettings.order });
      }
      return orderSettings.order;
    } catch (e) {
      this.logger.warn("Could not load stored columns order. Falling back to default order", e);
    }
  }

  return defaultColumnsOrder();
}

export function loadColumnsVisibility({ pageName }) {
  let storageItem = localStorage.getItem(columnsVisibilityKey({ pageName }));

  if (!storageItem) {
    // Version 12 introduced a new key name to support saving settings based on orgRole.
    // We check if there are settings using the old key so we can migrate the settings.
    storageItem = localStorage.getItem(`settings.transportTable.${pageName}.columns`);
  }

  if (storageItem) {
    try {
      let columnSettings = JSON.parse(storageItem);
      if (columnSettings.version !== VERSION) {
        columnSettings = migrateColumns({ columnSettings, pageName });
        saveColumnsVisibilityForPage({ pageName, columns: columnSettings.columns });
      }
      return columnSettings.columns;
    } catch (e) {
      this.logger.warn("Could not load stored column settings. Falling back to default settings", e);
    }
  }

  return defaultColumnsVisibility({ pageName });
}

export function saveColumnsOrder({ order }) {
  localStorage.setItem(columnsOrderKey(), JSON.stringify({ order, version: VERSION }));
  return order;
}

export function saveColumnsVisibilityForPage({ columns, pageName }) {
  localStorage.setItem(
    columnsVisibilityKey({ pageName }),
    JSON.stringify({
      columns,
      version: VERSION,
    })
  );

  return columns;
}

export function saveColumnsVisibilityForAll({ newColumnsSettings }) {
  const modifiedColumnSettings = {};

  // We check each page settings and only change the columns that the page
  // should have and avoid adding columns they shouldn't have.
  Object.values(TRANSPORT_PAGES).forEach((pageName) => {
    const modifiedColumns = loadColumnsVisibility({ pageName }).map((columnSetting) => {
      const columnIndex = newColumnsSettings.findIndex((column) => column.key === columnSetting.key);
      if (columnIndex > -1) {
        columnSetting.enabled = newColumnsSettings[columnIndex].enabled;
      }
      return columnSetting;
    });

    saveColumnsVisibilityForPage({ columns: modifiedColumns, pageName });

    modifiedColumnSettings[pageName] = modifiedColumns;
  });

  return modifiedColumnSettings;
}

function columnsOrderKey() {
  return `settings.transportsTable.order`;
}

function columnsVisibilityKey({ pageName }) {
  return `settings.transportsTable.${pageName}.columns`;
}
